import { Button, CircularProgress, MenuItem, TextField } from '@mui/material';
import { Gauge, RefreshCw } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { listAgents, listIntegrationClients } from '../../services/api';
import { getThirdPartyUsage } from '../../services/third-party-usage.service';
import type {
  Agent,
  IntegrationClient,
  ThirdPartyUsageGroupBy,
  ThirdPartyUsageRow,
} from '../../types';
import { useNotification } from '../../hooks/useNotification';
import { useTranslation } from 'react-i18next';
import { UsageStat } from './usageShared';
import {
  defaultUsageFromDate,
  defaultUsageToDate,
  THIRD_PARTY_USAGE_GROUPS,
  usageRowLabel,
} from './usageUtils';

export const ThirdPartyUsagePanel: React.FC = () => {
  const { t } = useTranslation();
  const { error } = useNotification();
  const [clients, setClients] = useState<IntegrationClient[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [rows, setRows] = useState<ThirdPartyUsageRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [groupBy, setGroupBy] = useState<ThirdPartyUsageGroupBy>('external_tenant');
  const [from, setFrom] = useState(defaultUsageFromDate);
  const [to, setTo] = useState(defaultUsageToDate);
  const [clientId, setClientId] = useState('');
  const [agentPublicId, setAgentPublicId] = useState('');
  const [externalTenantId, setExternalTenantId] = useState('');

  const totals = rows.reduce(
    (sum, row) => ({
      messages: sum.messages + row.messages,
      tokens: sum.tokens + row.total_tokens,
      credits: sum.credits + row.cost_credits,
      toolCalls: sum.toolCalls + row.tool_calls,
    }),
    { messages: 0, tokens: 0, credits: 0, toolCalls: 0 }
  );

  const loadFilterResources = async () => {
    setFiltersLoading(true);
    const [clientsRes, agentsRes] = await Promise.all([listIntegrationClients(), listAgents()]);

    if (clientsRes.success && clientsRes.data) {
      setClients(clientsRes.data);
    }

    if (agentsRes.success && agentsRes.data) {
      setAgents(agentsRes.data);
    }

    setFiltersLoading(false);
  };

  const loadUsage = async () => {
    setLoading(true);

    try {
      const response = await getThirdPartyUsage({
        groupBy,
        from: from ? new Date(from).toISOString() : undefined,
        to: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
        clientId: clientId || undefined,
        agentPublicId: agentPublicId || undefined,
        externalTenantId: externalTenantId.trim() || undefined,
      });
      setRows(response.data ?? []);
    } catch (err) {
      setRows([]);
      error(err instanceof Error ? err.message : t('apiKeys.errors.loadUsageFailed'));
    }

    setLoading(false);
  };

  useEffect(() => {
    void loadFilterResources();
  }, []);

  useEffect(() => {
    void loadUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy, from, to, clientId, agentPublicId]);

  return (
    <section className="mb-6 rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Gauge className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
              {t('apiKeys.usage.title')}
            </h2>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
            {t('apiKeys.usage.subtitle')}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <UsageStat label={t('apiKeys.usage.messages')} value={totals.messages} />
          <UsageStat label={t('apiKeys.usage.tokens')} value={totals.tokens} />
          <UsageStat label={t('apiKeys.usage.credits')} value={totals.credits} />
          <UsageStat label={t('apiKeys.usage.toolCalls')} value={totals.toolCalls} />
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-[150px_150px_180px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto]">
        <TextField
          fullWidth
          size="small"
          type="date"
          label={t('apiKeys.usage.from')}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          fullWidth
          size="small"
          type="date"
          label={t('apiKeys.usage.to')}
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          select
          fullWidth
          size="small"
          label={t('apiKeys.usage.groupBy')}
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as ThirdPartyUsageGroupBy)}
        >
          {THIRD_PARTY_USAGE_GROUPS.map((group) => (
            <MenuItem key={group} value={group}>
              {t(`apiKeys.usage.groups.${group}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          size="small"
          label={t('apiKeys.modal.integrationClientResource')}
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          disabled={filtersLoading}
        >
          <MenuItem value="">{t('apiKeys.usage.allClients')}</MenuItem>
          {clients.map((client) => (
            <MenuItem key={client.public_id} value={client.public_id}>
              {client.name} · {client.public_id}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          size="small"
          label={t('apiKeys.modal.agentResource')}
          value={agentPublicId}
          onChange={(e) => setAgentPublicId(e.target.value)}
          disabled={filtersLoading}
        >
          <MenuItem value="">{t('apiKeys.usage.allAgents')}</MenuItem>
          {agents.map((agent) => (
            <MenuItem key={agent.publicId} value={agent.publicId}>
              {agent.name} · {agent.publicId}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          size="small"
          label={t('apiKeys.usage.externalTenantId')}
          placeholder="salon_456"
          value={externalTenantId}
          onChange={(e) => setExternalTenantId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              void loadUsage();
            }
          }}
        />
        <Button
          type="button"
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshCw size={16} />}
          onClick={() => void loadUsage()}
          disabled={loading}
        >
          {t('apiKeys.usage.apply')}
        </Button>
      </div>

      <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700">
        <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_110px_120px_120px_110px] bg-gray-50 text-xs font-semibold text-gray-500 dark:bg-slate-900 dark:text-slate-400">
          <div className="p-2">{t('apiKeys.usage.group')}</div>
          <div className="p-2">{t('apiKeys.usage.context')}</div>
          <div className="p-2">{t('apiKeys.usage.messages')}</div>
          <div className="p-2">{t('apiKeys.usage.tokens')}</div>
          <div className="p-2">{t('apiKeys.usage.credits')}</div>
          <div className="p-2">{t('apiKeys.usage.toolCalls')}</div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 border-t border-gray-100 p-4 text-sm text-gray-500 dark:border-slate-700 dark:text-slate-400">
            <CircularProgress size={18} />
            {t('apiKeys.usage.loading')}
          </div>
        ) : rows.length === 0 ? (
          <div className="border-t border-gray-100 p-4 text-center text-sm text-gray-500 dark:border-slate-700 dark:text-slate-400">
            {t('apiKeys.usage.empty')}
          </div>
        ) : (
          rows.map((row, index) => (
            <div
              key={`${usageRowLabel(row, groupBy)}-${index}`}
              className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_110px_120px_120px_110px] border-t border-gray-100 text-xs dark:border-slate-700"
            >
              <div className="min-w-0 p-2">
                <div className="truncate font-semibold text-gray-800 dark:text-slate-200">
                  {usageRowLabel(row, groupBy)}
                </div>
                <div className="truncate text-gray-400 dark:text-slate-500">
                  {row.external_tenant_type || groupBy}
                </div>
              </div>
              <div className="min-w-0 p-2 text-gray-500 dark:text-slate-400">
                <div className="truncate">{row.client_id || 'client:-'}</div>
                <div className="truncate">{row.agent_public_id || 'agent:-'}</div>
              </div>
              <div className="p-2 text-gray-700 dark:text-slate-300">
                {row.messages.toLocaleString()}
              </div>
              <div className="p-2 text-gray-700 dark:text-slate-300">
                {row.total_tokens.toLocaleString()}
              </div>
              <div className="p-2 text-gray-700 dark:text-slate-300">
                {row.cost_credits.toLocaleString()}
              </div>
              <div className="p-2 text-gray-700 dark:text-slate-300">
                {row.tool_calls.toLocaleString()}
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
};
